import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { QuestionCircleIcon } from '../../components/icons/QuestionCircleIcon';
import { t } from '../utils/translate';
import styles from './InfoTooltip.module.less';

interface Props {
  content: React.ReactNode;
  ariaLabel?: string;
}

export function InfoTooltip({ content, ariaLabel }: Props) {
  const intl = useIntl();
  const label = ariaLabel ?? t(intl, 'calculator.moreInfo');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !tooltipRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <span className={styles.container}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <QuestionCircleIcon color="currentColor" />
      </button>
      {open && (
        <div ref={tooltipRef} className={styles.tooltip} role="tooltip">
          {content}
        </div>
      )}
    </span>
  );
}
